import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { CompanyStatusBadge } from '@l2u/ui/components/StatusBadge.tsx';
import { SearchInput } from '../components/SearchInput.tsx';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect.tsx';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import {
  distinctVendors,
  expiringFiltersActive,
  filterExpiringRows,
  parseExpiringFilters,
  type ExpiringFilters,
} from '../lib/expiringFilters.ts';
import { reconcileCompanyFilter } from '../lib/companyFilter.ts';
import { companyFilterOptions } from '../lib/companyFilterOptions.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import {
  clearStoredFilter,
  pruneToOptions,
  readStoredFilter,
  writeStoredFilter,
} from '../lib/filterStorage.ts';
import { formatDateOnly } from '@l2u/ui/lib/expiry.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { buildExpiringCsv } from '../lib/expiringCsv.ts';
import type { AdminCompanyListRow, CompanyStatus } from '@l2u/ui/lib/types.ts';

type ExpiringRow = {
  id: string;
  file_name: string;
  file_path: string;
  expires_at: string;
  created_at: string;
  option_key: string | null;
  submission: {
    id: string;
    question_id: string;
    company_id: string;
    company: {
      id: string;
      company_name: string;
      contact_name: string;
      email: string;
      status: CompanyStatus;
    } | null;
    question: { id: string; label: string } | null;
  } | null;
};

type ExpiringResponse = {
  expired: ExpiringRow[];
  expiring: ExpiringRow[];
  horizon_days: number;
};

const EMPTY_FILTERS: ExpiringFilters = {
  company: { contractorTypes: [], clusterRegions: [], stations: [] },
  query: '',
  vendorIds: [],
  status: 'all',
  expiryFrom: '',
  expiryTo: '',
};

const EXPIRING_FILTERS_KEY = 'expiringDocuments.filters';

export function AdminExpiringDocuments() {
  // Hydration is synchronous — seeded in the useState initializer, never
  // copied in from an effect — so the controls render preselected on the very
  // first paint (pinned by filterHydration.test.tsx).
  const [filters, setFilters] = useState<ExpiringFilters>(
    () => readStoredFilter(EXPIRING_FILTERS_KEY, parseExpiringFilters) ?? EMPTY_FILTERS,
  );

  const { settings, loading: settingsLoading } = usePortalSettings();
  const { user } = useAdminAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['expiring-documents'],
    queryFn: () => adminApi.get<ExpiringResponse>('/api/companies/files/expiring?days=30'),
  });

  // Reuse the companies list (shared query key with Dashboard — already in cache)
  // to join contractor_types / cluster_regions / stations onto each document row.
  const { data: companiesData, isSuccess: companiesLoaded } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: AdminCompanyListRow[] }>('/api/companies'),
  });

  // Build a lookup map from company id → filterable fields. Hooks must come
  // before any early return so the call order is stable across renders.
  const companyById = useMemo(() => {
    const map = new Map<string, AdminCompanyListRow>();
    for (const c of companiesData?.companies ?? []) map.set(c.id, c);
    return map;
  }, [companiesData]);

  // Settings-curated lists for all three dimensions, narrowed to the signed-in
  // user's scope where one applies — see companyFilterOptions for why cluster
  // region is neither scoped nor derived from the loaded rows. Deliberately
  // independent of companiesData: the options are the configured lists, not
  // what is currently in use.
  const filterOptions = useMemo(() => companyFilterOptions(settings, user), [settings, user]);

  // Write-through: persist every change; an all-empty filter set clears the key
  // so "Clear all" can never be resurrected on the next mount.
  useEffect(() => {
    if (expiringFiltersActive(filters)) writeStoredFilter(EXPIRING_FILTERS_KEY, filters);
    else clearStoredFilter(EXPIRING_FILTERS_KEY);
  }, [filters]);

  // Reconcile the restored company dimensions against the live option list —
  // but only once Settings has resolved (pruning against the empty
  // loading-window list would wipe them, the same bug the companiesLoaded guard
  // below protects row filtering from). Explicit rather than left to
  // orderedSelected: the pruned set flows back to storage via the write-through
  // effect above, so a restored dropdown can never show a badge count the panel
  // doesn't have. reconcileCompanyFilter additionally refuses to prune against
  // an all-empty option set (a failed settings fetch is indistinguishable from
  // "nothing configured") and returns the same reference when nothing changed,
  // so this can neither wipe a filter on a network blip nor loop.
  // The region options now come from the companies join, so pruning waits on
  // that query too — otherwise a stored region filter would be wiped during the
  // loading window.
  useEffect(() => {
    if (settingsLoading || !companiesLoaded) return;
    setFilters((f) => {
      const company = reconcileCompanyFilter(f.company, filterOptions);
      return company === f.company ? f : { ...f, company };
    });
  }, [settingsLoading, companiesLoaded, filterOptions]);

  // Same reconciliation for the vendor ids, but their option source is NOT
  // Settings — it's distinctVendors over the expiring-documents rows. So this
  // waits on that query instead: `data` is undefined while it's in flight AND
  // on error, so a restored vendor selection is never pruned against a list
  // that simply hasn't arrived.
  useEffect(() => {
    if (!data) return;
    const available = distinctVendors([...data.expired, ...data.expiring]).map((v) => v.id);
    setFilters((f) => {
      const vendorIds = pruneToOptions(f.vendorIds, available);
      // pruneToOptions only ever removes, so equal lengths imply equal sets.
      return vendorIds.length === f.vendorIds.length ? f : { ...f, vendorIds };
    });
  }, [data]);

  if (isLoading) return <CenteredSpinner />;

  const expired = data?.expired ?? [];
  const expiring = data?.expiring ?? [];

  // Vendor options come from BOTH sets so the dropdown is stable as the status
  // filter narrows the table.
  const vendors = distinctVendors([...expired, ...expiring]);
  const vendorIdValues = vendors.map((v) => v.id);
  const vendorById = new Map(vendors.map((v) => [v.id, v]));

  const filtersActive = expiringFiltersActive(filters);

  // Don't apply the company-dimension filter until the companies query has loaded
  // (otherwise every row would drop while it is in flight). query / vendor / date
  // don't depend on it.
  const appliedFilters: ExpiringFilters = companiesLoaded
    ? filters
    : { ...filters, company: EMPTY_FILTERS.company };

  const showExpired = filters.status !== 'expiring';
  const showExpiring = filters.status !== 'expired';

  const filteredExpired = showExpired
    ? filterExpiringRows(expired, appliedFilters, companyById)
    : [];
  const filteredExpiring = showExpiring
    ? filterExpiringRows(expiring, appliedFilters, companyById)
    : [];

  // Task 23: export exactly the rows currently on screen. Client-side — the
  // list is already loaded, so no API round-trip. Mirrors the vendors export.
  function exportCsv() {
    const csv = buildExpiringCsv(
      [...filteredExpired, ...filteredExpiring],
      companyById,
      new Date(),
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expiring-documents-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Expiring documents</h1>
          <p className="mt-1 text-sm text-slate-500">
            Uploaded documents that have expired or are due to expire within{' '}
            {data?.horizon_days ?? 30} days. Vendors with expired documents are flagged
            non-compliant.
          </p>
        </div>
        <Btn onClick={exportCsv} disabled={filteredExpired.length + filteredExpiring.length === 0}>
          Export CSV
        </Btn>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        {/* Search + every filter share one wrapping row (mirrors the Dashboard
            and Vendors list); the search box flexes to fill the leftover width
            on sm+ and takes its own line, with controls wrapping below, on
            narrow viewports. */}
        {/* Each control grows (flex-1) to an equal share of its line so the row
            fills the width evenly with no trailing gap; min-widths force a clean
            wrap instead of one squished line. Search gets a 2× grow so it stays
            the widest control. The three dimension dropdowns are inlined (rather
            than via the shared FilterBar) so each is its own growing cell. */}
        <div className="flex flex-wrap items-stretch gap-2">
          <SearchInput
            className="w-full sm:w-auto sm:flex-[2] sm:min-w-[16rem]"
            value={filters.query}
            onChange={(query) => setFilters((f) => ({ ...f, query }))}
            placeholder="Search documents, vendors or questions…"
          />

          {/* Contractor type / cluster region / station dimensions lead the
              filter set; the document-specific filters follow. */}
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Contractor type"
            unitLabel="contractor types"
            values={filterOptions.contractorTypes}
            selected={filters.company.contractorTypes}
            onChange={(next) =>
              setFilters((f) => ({ ...f, company: { ...f.company, contractorTypes: next } }))
            }
          />
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Cluster region"
            unitLabel="cluster regions"
            values={filterOptions.clusterRegions}
            selected={filters.company.clusterRegions}
            onChange={(next) =>
              setFilters((f) => ({ ...f, company: { ...f.company, clusterRegions: next } }))
            }
          />
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Station"
            unitLabel="stations"
            values={filterOptions.stations}
            selected={filters.company.stations}
            onChange={(next) =>
              setFilters((f) => ({ ...f, company: { ...f.company, stations: next } }))
            }
          />
          <SearchableMultiSelect
            className="sm:flex-1 sm:min-w-[11rem]"
            label="Vendor"
            unitLabel="vendors"
            values={vendorIdValues}
            selected={filters.vendorIds}
            onChange={(vendorIds) => setFilters((f) => ({ ...f, vendorIds }))}
            labelFor={(id) => vendorById.get(id)?.name ?? id}
            subtitleFor={(id) => vendorById.get(id)?.email}
          />

          {/* Document status segmented control */}
          <div className="flex overflow-hidden rounded-md border border-slate-200 sm:flex-1 sm:min-w-[11rem]">
            {(['all', 'expired', 'expiring'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, status: s }))}
                className={`flex-1 whitespace-nowrap px-3 py-2 text-sm transition ${
                  filters.status === s
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {s === 'all' ? 'All' : s === 'expired' ? 'Expired' : 'Expiring soon'}
              </button>
            ))}
          </div>

          {/* Expiry date range */}
          <label className="flex items-center gap-1 text-sm text-slate-600 sm:flex-1 sm:min-w-[9rem]">
            <span className="text-xs text-slate-500">From</span>
            <input
              type="date"
              value={filters.expiryFrom}
              onChange={(e) => setFilters((f) => ({ ...f, expiryFrom: e.target.value }))}
              className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex items-center gap-1 text-sm text-slate-600 sm:flex-1 sm:min-w-[9rem]">
            <span className="text-xs text-slate-500">To</span>
            <input
              type="date"
              value={filters.expiryTo}
              onChange={(e) => setFilters((f) => ({ ...f, expiryTo: e.target.value }))}
              className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          </label>

          {filtersActive && (
            <button
              type="button"
              className="rounded-md px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <Section
        title="Expired"
        rows={filteredExpired}
        emptyHint={
          filtersActive
            ? 'No expired documents match the current filters.'
            : 'No expired documents.'
        }
        tone="danger"
      />
      <Section
        title="Expiring soon"
        rows={filteredExpiring}
        emptyHint={
          filtersActive
            ? 'No upcoming expiries match the current filters.'
            : 'No upcoming expiries in the next 30 days.'
        }
        tone="warn"
      />
    </div>
  );
}

function Section({
  title,
  rows,
  emptyHint,
  tone,
}: {
  title: string;
  rows: ExpiringRow[];
  emptyHint: string;
  tone: 'danger' | 'warn';
}) {
  if (rows.length === 0) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
        <EmptyState title={title} description={emptyHint} />
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title} <span className="text-slate-400">({rows.length})</span>
      </h2>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Document</th>
              <th className="px-4 py-2">Vendor</th>
              <th className="px-4 py-2">Question</th>
              <th className="px-4 py-2">Expires</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const company = row.submission?.company;
              const companyId = company?.id ?? row.submission?.company_id;
              const questionId = row.submission?.question_id;
              // Deep-link to the company's document/review view. When a question
              // id is available, append ?question=<id> so the admin lands on
              // the specific document that needs updating (mirrors the vendor's
              // ?question= deep-link pattern).
              const companyHref = companyId
                ? questionId
                  ? `/vendors/${companyId}?question=${questionId}`
                  : `/vendors/${companyId}`
                : null;
              return (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">
                    {companyHref ? (
                      <Link to={companyHref} className="hover:underline">
                        {row.file_name}
                      </Link>
                    ) : (
                      row.file_name
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {company ? (
                      <Link to={`/vendors/${company.id}`} className="hover:underline">
                        {company.company_name}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {company && <div className="text-xs text-slate-500">{company.email}</div>}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {row.submission?.question?.label ?? '—'}
                  </td>
                  <td
                    className={`px-4 py-2 ${tone === 'danger' ? 'text-red-700' : 'text-amber-700'}`}
                  >
                    {formatDateOnly(row.expires_at)}
                  </td>
                  <td className="px-4 py-2">
                    {company ? <CompanyStatusBadge status={company.status} /> : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
